import HttpError from '@shared/api-client/HttpError';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/component/Toast';
import { track } from '@/feature/tracking';
import { useTexts } from '../lang';
import {
  changePassword,
  changePasswordForUser,
  createUser,
  softDeleteUser,
  updateUser,
} from './api';
import { useCurrentUser } from './queries';

export const useCreateUserMutation = () => {
  const syncCache = useSyncCache();
  const { success, error } = useToast();
  const { admin } = useTexts();

  return useMutation<UserDto, HttpError, Partial<UserDto>>({
    mutationFn: createUser,
    onSuccess: (user) => {
      syncCache(user);
      success(admin.user.invite.notification.success, {
        values: {
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
        },
      });
      track('user_created', { user_roles: user.roles });
    },
    onError: (e) => {
      error(admin.user.invite.notification.error, {
        values: { message: e.message },
      });
    },
  });
};

export const useChangePasswordMutation = () => {
  return useMutation({ mutationFn: changePassword });
};

export const useChangePasswordForUserMutation = () => {
  const { success } = useToast();
  const { admin } = useTexts();

  return useMutation({
    mutationFn: changePasswordForUser,
    onSuccess: () => success(admin.user.notification.passwordChanged),
  });
};

const useUpdateUserMutation = () => {
  const syncCache = useSyncCache();

  return useMutation({
    mutationFn: updateUser,
    onSuccess: (user) => syncCache(user),
  });
};

export const useUpdateUser = () => {
  const { mutateAsync: update, status } = useUpdateUserMutation();

  return {
    update,
    mutationStatus: status,
  };
};

export const useSoftDeleteUserMutation = () => {
  const syncCache = useSyncCache();
  return useMutation({
    mutationFn: softDeleteUser,
    onSuccess: () => syncCache(),
  });
};

function useSyncCache() {
  const queryClient = useQueryClient();
  const { id: currentUserId } = useCurrentUser();

  return (user?: UserDto) => {
    queryClient.invalidateQueries({ queryKey: ['users'] });
    queryClient.invalidateQueries({ queryKey: ['companies'] });

    if (user) {
      queryClient.invalidateQueries({ queryKey: ['user', user.id] });
      const isCurrentUserUpdated = user.id === currentUserId;
      if (isCurrentUserUpdated) {
        queryClient.setQueryData(['currentUser'], user);
      }
    }
  };
}
